import React, { createContext, useContext, useState, useCallback } from 'react';

export interface EditorElement {
  id: string;
  type: string;
  props?: Record<string, unknown>;
  children?: EditorElement[];
  position?: { x: number; y: number };
  size?: { width: string; height: string };
  styles?: Record<string, string>;
  [key: string]: unknown;
}

interface EditorContextValue {
  selectedElement: EditorElement | null;
  elements: EditorElement[];
  isPreviewMode: boolean;
  isDirty: boolean;
  canUndo: boolean;
  canRedo: boolean;
  setSelectedElement: React.Dispatch<React.SetStateAction<EditorElement | null>>;
  addElement: (element: Partial<EditorElement>) => EditorElement;
  updateElement: (elementId: string, updates: Partial<EditorElement>) => void;
  removeElement: (elementId: string) => void;
  moveElement: (elementId: string, newPosition: { x: number; y: number }) => void;
  resizeElement: (elementId: string, newSize: { width: string; height: string }) => void;
  duplicateElement: (elementId: string) => void;
  undo: () => void;
  redo: () => void;
  clearCanvas: () => void;
  saveProject: (projectName: string) => { name: string; elements: EditorElement[]; createdAt: string; updatedAt: string };
  loadProject: (projectName: string) => { name: string; elements: EditorElement[]; createdAt: string; updatedAt: string } | null;
  exportToHTML: () => void;
  togglePreviewMode: () => void;
  generateHTML: (elements: EditorElement[]) => string;
}

const EditorContext = createContext<EditorContextValue | null>(null);

export const useEditor = (): EditorContextValue => {
  const context = useContext(EditorContext);
  if (!context) {
    throw new Error('useEditor deve ser usado dentro de um EditorProvider');
  }
  return context;
};

interface EditorProviderProps {
  children: React.ReactNode;
}

export const EditorProvider: React.FC<EditorProviderProps> = ({ children }) => {
  const [selectedElement, setSelectedElement] = useState<EditorElement | null>(null);
  const [elements, setElements] = useState<EditorElement[]>([]);
  const [history, setHistory] = useState<EditorElement[][]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const saveToHistory = useCallback((newElements: EditorElement[]) => {
    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(JSON.parse(JSON.stringify(newElements)));
      return newHistory.slice(-50);
    });
    setHistoryIndex(prev => Math.min(prev + 1, 49));
  }, [historyIndex]);

  const addElement = useCallback((element: Partial<EditorElement>) => {
    const newElement: EditorElement = {
      id: `element_${Date.now()}`,
      type: element.type ?? 'div',
      props: element.props ?? {},
      children: element.children ?? [],
      position: element.position ?? { x: 0, y: 0 },
      size: element.size ?? { width: 'auto', height: 'auto' },
      styles: element.styles ?? {},
      ...element,
    } as EditorElement;

    setElements(prev => {
      const newElements = [...prev, newElement];
      saveToHistory(newElements);
      setIsDirty(true);
      return newElements;
    });

    setSelectedElement(newElement);
    return newElement;
  }, [saveToHistory]);

  const updateElement = useCallback((elementId: string, updates: Partial<EditorElement>) => {
    setElements(prev => {
      const newElements = prev.map(element =>
        element.id === elementId ? { ...element, ...updates } : element
      );
      saveToHistory(newElements);
      setIsDirty(true);
      return newElements;
    });

    if (selectedElement?.id === elementId) {
      setSelectedElement(prev => (prev ? { ...prev, ...updates } : null));
    }
  }, [selectedElement, saveToHistory]);

  const removeElement = useCallback((elementId: string) => {
    setElements(prev => {
      const newElements = prev.filter(element => element.id !== elementId);
      saveToHistory(newElements);
      setIsDirty(true);
      return newElements;
    });

    if (selectedElement?.id === elementId) {
      setSelectedElement(null);
    }
  }, [selectedElement, saveToHistory]);

  const moveElement = useCallback((elementId: string, newPosition: { x: number; y: number }) => {
    updateElement(elementId, { position: newPosition });
  }, [updateElement]);

  const resizeElement = useCallback((elementId: string, newSize: { width: string; height: string }) => {
    updateElement(elementId, { size: newSize });
  }, [updateElement]);

  const duplicateElement = useCallback((elementId: string) => {
    const element = elements.find(el => el.id === elementId);
    if (element) {
      const duplicatedElement: EditorElement = {
        ...element,
        id: `element_${Date.now()}`,
        position: {
          x: (element.position?.x ?? 0) + 20,
          y: (element.position?.y ?? 0) + 20,
        },
      } as EditorElement;
      addElement(duplicatedElement);
    }
  }, [elements, addElement]);

  const undo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setElements(JSON.parse(JSON.stringify(history[newIndex])));
      setIsDirty(true);
    }
  }, [historyIndex, history]);

  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setElements(JSON.parse(JSON.stringify(history[newIndex])));
      setIsDirty(true);
    }
  }, [historyIndex, history]);

  const clearCanvas = useCallback(() => {
    setElements([]);
    setSelectedElement(null);
    saveToHistory([]);
    setIsDirty(true);
  }, [saveToHistory]);

  const saveProject = useCallback((projectName: string) => {
    const project = {
      name: projectName,
      elements,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    localStorage.setItem(`project_${projectName}`, JSON.stringify(project));
    setIsDirty(false);
    return project;
  }, [elements]);

  const loadProject = useCallback((projectName: string) => {
    const projectData = localStorage.getItem(`project_${projectName}`);
    if (projectData) {
      const project = JSON.parse(projectData) as { elements?: EditorElement[] };
      const loadedElements = project.elements ?? [];
      setElements(loadedElements);
      setSelectedElement(null);
      saveToHistory(loadedElements);
      setIsDirty(false);
      return {
        name: projectName,
        elements: loadedElements,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
    }
    return null;
  }, [saveToHistory]);

  const generateHTML = useCallback((elementsToExport: EditorElement[]) => {
    const generateElementHTML = (element: EditorElement): string => {
      const { type, props = {}, children = [], styles = {} } = element;
      const styleString = Object.entries(styles)
        .map(([key, value]) => `${key}: ${value}`)
        .join('; ');

      const propsString = Object.entries(props)
        .map(([key, value]) => `${key}="${value}"`)
        .join(' ');

      const childrenHTML = children
        .map(child => generateElementHTML(child as EditorElement))
        .join('');

      return `<${type} ${propsString} style="${styleString}">${childrenHTML}</${type}>`;
    };

    return `
<!DOCTYPE html>
<html lang="pt-BR">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Projeto Exportado</title>
    <style>
        body { margin: 0; padding: 20px; font-family: Arial, sans-serif; }
        .container { max-width: 1200px; margin: 0 auto; }
    </style>
</head>
<body>
    <div class="container">
        ${elementsToExport.map(element => generateElementHTML(element)).join('')}
    </div>
</body>
</html>`;
  }, []);

  const exportToHTML = useCallback(() => {
    const htmlContent = generateHTML(elements);
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'projeto.html';
    a.click();
    URL.revokeObjectURL(url);
  }, [elements, generateHTML]);

  const togglePreviewMode = useCallback(() => {
    setIsPreviewMode(prev => !prev);
    if (!isPreviewMode) {
      setSelectedElement(null);
    }
  }, [isPreviewMode]);

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  const value: EditorContextValue = {
    selectedElement,
    elements,
    isPreviewMode,
    isDirty,
    canUndo,
    canRedo,
    setSelectedElement,
    addElement,
    updateElement,
    removeElement,
    moveElement,
    resizeElement,
    duplicateElement,
    undo,
    redo,
    clearCanvas,
    saveProject,
    loadProject,
    exportToHTML,
    togglePreviewMode,
    generateHTML,
  };

  return (
    <EditorContext.Provider value={value}>
      {children}
    </EditorContext.Provider>
  );
};
